export const todayString = (now = new Date()) => (
  `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
);

export const shiftDate = (dateString, days) => {
  const date = new Date(`${dateString}T12:00:00`);
  date.setDate(date.getDate() + days);
  return todayString(date);
};

export const formatDateHeading = (dateString, now = new Date()) => {
  const today = todayString(now);
  if (dateString === today) return 'Today';
  if (dateString === shiftDate(today, -1)) return 'Yesterday';
  return new Date(`${dateString}T12:00:00`).toLocaleDateString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric',
  });
};

export const formatDeliveryTime = (value) => {
  if (!value) return '';
  try {
    return new Date(value).toLocaleTimeString('en-US', {
      hour: 'numeric', minute: '2-digit', hour12: true,
    });
  } catch {
    return '';
  }
};

export const orderNumberSuffix = (number) => number?.split('-').pop() || '—';

export const collectDeliveryDrivers = (orders) => {
  const drivers = new Map();
  orders.forEach((order) => {
    const confirmation = order.delivery_confirmation || {};
    if (confirmation.confirmed_by_user_id && confirmation.confirmed_by_user_name) {
      drivers.set(confirmation.confirmed_by_user_id, confirmation.confirmed_by_user_name);
    }
    if (order.assigned_driver_name && !Array.from(drivers.values()).includes(order.assigned_driver_name)) {
      drivers.set(`assigned_${order.assigned_driver_name}`, order.assigned_driver_name);
    }
  });
  return Array.from(drivers.entries());
};

export const collectDeliverySources = (orders) => (
  Array.from(new Set(orders.map((order) => order.resolved_source).filter(Boolean))).sort()
);

export const collectDeliveryAddresses = (orders) => {
  const addresses = new Map();
  orders.forEach((order) => {
    const street = order.recipient_street_address;
    if (!street || addresses.has(street.toLowerCase())) return;
    addresses.set(street.toLowerCase(), {
      street,
      city: order.recipient_city,
      full: [street, order.recipient_city, order.recipient_state].filter(Boolean).join(', '),
    });
  });
  return Array.from(addresses.values());
};

export const findAddressSuggestions = (addresses, search, limit = 8) => {
  if (!search || search.length < 2) return [];
  const query = search.toLowerCase();
  return addresses.filter((address) => address.full.toLowerCase().includes(query)).slice(0, limit);
};

export const orderIdsForRun = (runs, runId) => {
  if (runId === 'all') return null;
  const run = runs.find((candidate) => candidate.id === runId);
  if (!run) return new Set();
  return new Set((run.stops || []).map((stop) => stop.order_id));
};

export const filterDeliveryOrders = ({
  orders,
  sourceFilter,
  driverFilter,
  drivers,
  runOrderIds,
  nameSearch,
  addressSearch,
  selectedDate,
}) => {
  let result = orders;
  if (sourceFilter !== 'all') {
    result = result.filter((order) => order.resolved_source === sourceFilter);
  }
  if (driverFilter !== 'all') {
    const driverName = drivers.find(([id]) => id === driverFilter)?.[1];
    if (!driverName) return [];
    result = result.filter((order) => (
      order.delivery_confirmation?.confirmed_by_user_name === driverName
      || order.assigned_driver_name === driverName
    ));
  }
  if (runOrderIds) {
    result = result.filter((order) => runOrderIds.has(order.id));
  }
  if (selectedDate && nameSearch.trim()) {
    const query = nameSearch.trim().toLowerCase();
    result = result.filter((order) => (order.recipient_name || '').toLowerCase().includes(query));
  }
  if (selectedDate && addressSearch.trim()) {
    const query = addressSearch.trim().toLowerCase();
    result = result.filter((order) => (
      [
        order.recipient_street_address,
        order.recipient_city,
        order.recipient_state,
        order.recipient_zip_code,
      ].filter(Boolean).join(', ').toLowerCase().includes(query)
    ));
  }
  return result;
};

export const groupDeliveryOrders = (orders) => {
  const groups = { delivered: [], onRoute: [], failed: [], undelivered: [] };
  orders.forEach((order) => {
    if (order.status === 'delivered') groups.delivered.push(order);
    else if (order.status === 'on_route') groups.onRoute.push(order);
    else if (order.status === 'failed') groups.failed.push(order);
    else groups.undelivered.push(order);
  });
  const sortByNumber = (a, b) => (
    (a.internal_order_number || '').localeCompare(b.internal_order_number || '')
  );
  Object.values(groups).forEach((group) => group.sort(sortByNumber));
  return groups;
};
